import { ObservableMap } from 'mobx';
import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import type { SessionUserConfiguration } from 'owa-service/lib/types/SessionUserConfiguration';
import { deletedUserConfigurationSessionSettingsProperties } from 'owa-service/lib/types/SessionUserConfiguration';
import type { OwaSessionStore } from '../store/schema/OwaSessionStore';
import { extractAccountScopeSettingsFromOwaUserConfiguration } from './extractAccountScopeSettingsFromOwaUserConfiguration';

export function convertOwaUserConfigurationToSessionStore(
    sourceId: string,
    owaUserConfig: OwaUserConfiguration
): OwaSessionStore {
    const sessionSettings = owaUserConfig.SessionSettings;

    const legacyFolderMapFolderNames = sessionSettings?.DefaultFolderNames;
    const legacyFolderMapFolderIds = sessionSettings?.DefaultFolderIds;

    // Until DefaultFolder properties move into AccountScopeUserSettings and gain the benefit of the data driven
    // property deletions inside extractAccountScopeSettingsFromOwaUserConfiguration, we need to manually delete them.
    delete sessionSettings?.DefaultFolderNames;
    delete sessionSettings?.DefaultFolderIds;

    // ATTENTION: This intentionally mutates the owaUserConfig object. In all likelihood you don't want to use
    // owaUserConfiguration after this. This is done to save an expensive OwaUserConfiguration copy while also
    // preventing extraneous non-type approprite properties being present on the observables.
    const accountSettings = extractAccountScopeSettingsFromOwaUserConfiguration(owaUserConfig);
    deletedUserConfigurationSessionSettingsProperties.forEach(key => {
        delete owaUserConfig.SessionSettings?.[key];
    });

    const sessionStore = {
        accountScopeUserSettingsMap: new ObservableMap({
            [sourceId]: accountSettings,
        }),
        userConfiguration: owaUserConfig as SessionUserConfiguration,

        defaultFolderIdToNameMap: new ObservableMap({}),
        defaultFolderNameToIdMap: new ObservableMap({}),
        connectedAccountsUserConfigurationMap: new ObservableMap({}),
    };

    // Until DefaultFolder properties move into AccountScopeUserSettings, populate the legacy folder map.
    if (legacyFolderMapFolderNames !== undefined && legacyFolderMapFolderIds !== undefined) {
        legacyFolderMapFolderNames.forEach((defaultFolderName, i) => {
            if (defaultFolderName) {
                const folderId = legacyFolderMapFolderIds[i];
                if (folderId) {
                    sessionStore.defaultFolderNameToIdMap.set(defaultFolderName, folderId.Id);
                    sessionStore.defaultFolderIdToNameMap.set(folderId.Id, defaultFolderName);
                }
            }
        });
    }

    return sessionStore;
}
